import { useState } from "react";
import { useLang } from "@/context/LanguageContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Users, Gift, ShieldOff, Gauge } from "lucide-react";
import PreToolDisclaimer from "@/components/PreToolDisclaimer";

export default function Pilot() {
  const { t, lang } = useLang();
  const [f, setF] = useState({ full_name: "", email: "", professional_field: "", country: "", organization: "", caseload_size: "", reason_to_join: "", consent_program: false });
  const [done, setDone] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.consent_program) return toast.error(t.consent.required);
    try {
      await api.post("/pilot", { ...f, language: lang });
      setDone(true); toast.success(t.pilot.success);
    } catch { toast.error("Submission failed"); }
  };

  const explainers = [
    { icon: Users, title: t.pilot.who_title, body: t.pilot.who_body, test: "pilot-who" },
    { icon: Gift, title: t.pilot.receive_title, body: t.pilot.receive_body, test: "pilot-receive" },
    { icon: ShieldOff, title: t.pilot.nopii_title, body: t.pilot.nopii_body, test: "pilot-nopii" },
    { icon: Gauge, title: t.pilot.feedback_title, body: t.pilot.feedback_body, test: "pilot-feedback-scope" },
  ];

  return (
    <div className="max-w-5xl mx-auto px-5 lg:px-8 py-20" data-testid="pilot-page">
      <h1 className="text-4xl sm:text-5xl font-bold">{t.pilot.title}</h1>
      <p className="mt-4 text-slate-300 max-w-3xl">{t.pilot.sub}</p>

      <PreToolDisclaimer />

      <div className="mt-10 grid gap-4 md:grid-cols-2">
        {explainers.map((e, i) => {
          const Ic = e.icon;
          return (
            <div key={i} className="card-mift p-6" data-testid={e.test}>
              <div className="w-10 h-10 rounded-lg bg-blue-500/10 border border-blue-500/30 flex items-center justify-center text-blue-300">
                <Ic className="w-5 h-5" />
              </div>
              <h3 className="mt-4 font-semibold text-slate-100 text-sm">{e.title}</h3>
              <p className="mt-2 text-sm text-slate-300 leading-relaxed">{e.body}</p>
            </div>
          );
        })}
      </div>

      {done ? (
        <div className="mt-10 card-mift p-8 max-w-3xl" data-testid="pilot-success">
          <p className="text-blue-300">{t.pilot.success}</p>
        </div>
      ) : (
        <form onSubmit={submit} className="mt-10 card-mift p-6 grid gap-4 max-w-3xl" data-testid="pilot-form">
          {[
            { k: "full_name", label: t.forms.full_name, req: true },
            { k: "email", label: t.forms.email, req: true, type: "email" },
            { k: "professional_field", label: t.forms.professional_field, req: true },
            { k: "country", label: t.forms.country, req: true },
            { k: "organization", label: t.forms.organization },
            { k: "caseload_size", label: t.forms.caseload_size },
          ].map((fd) => (
            <div key={fd.k}>
              <Label className="text-slate-300">{fd.label}{fd.req && " *"}</Label>
              <Input
                data-testid={`pilot-${fd.k}`}
                type={fd.type || "text"} required={fd.req}
                className="mt-1 bg-[#0A1224] border-slate-700 text-slate-100"
                value={f[fd.k]} onChange={(e) => set(fd.k, e.target.value)}
              />
            </div>
          ))}
          <div>
            <Label className="text-slate-300">{t.forms.reason_to_join} *</Label>
            <Textarea
              data-testid="pilot-reason" required
              className="mt-1 bg-[#0A1224] border-slate-700 text-slate-100" rows={4}
              value={f.reason_to_join} onChange={(e) => set("reason_to_join", e.target.value)}
            />
          </div>
          <label className="flex items-start gap-2 text-sm text-slate-300">
            <input type="checkbox" data-testid="pilot-consent" className="mt-1 accent-blue-500"
                   checked={f.consent_program} onChange={(e) => set("consent_program", e.target.checked)} />
            <span>{t.consent.program}</span>
          </label>
          <button type="submit" className="btn-primary w-max" data-testid="pilot-submit">{t.pilot.submit}</button>
        </form>
      )}
    </div>
  );
}
